import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import { DomainModel } from '@/models/proficiency/domain';
import { TxGradeBoundary } from '@/models/taxonomy/grade-boundary';
import { GradeModel } from '@/models/taxonomy/grade';
import { SubjectModel } from '@/models/taxonomy/subject';
import { competencyAPI } from '@/providers/apis/competency/competency';
import { taxonomyAPI } from '@/providers/apis/taxonomy/taxonomy';
import axios from 'axios';
import * as d3 from 'd3';

import { Domain } from '@/models/proficiency/domain-topic/domain';
import { Topic } from '@/models/proficiency/domain-topic/topic';
import { Competency } from '@/models/proficiency/domain-topic/competency';
import { getFWK } from '@/utils/taxonomy';
import { markersAPI } from '@/providers/apis/markers/markers';
import { MarkerListModel } from '@/models/marker/marker-list';
import { MarkerBoundaryModel } from '@/models/marker/marker';
import { markerSerilaizer } from '@/providers/serializers/markers/markers';
import { sessionService } from '@/providers/services/auth/session';
import { isAPICatchError, sortByProperty } from '@/utils/utils';

@Component({
  name: 'learner-proficiency-chart',
  components: {
    'google-material-icon': GoogleMaterialIcon,
  },
})

export default class LearnerProficiencyChart extends Vue {

  get chartHeight() {
    const component = this;
    const proficiencyChartData = component.chartData;
    const chartContainer = component.$el.querySelector('.scrollable-chart') as HTMLElement;
    const chartContainerHeight = chartContainer.offsetHeight;
    const chartHeight = component.isShowExpandedGraph ?
      component.highestTopicSize * component.expandedGraphCellHeight :
      chartContainerHeight;
    return chartContainerHeight || chartHeight - 15;
  }

  get chartWidth() {
    const component = this;
    const proficiencyChartData = component.chartData;
    const chartContainer = component.$el.querySelector('.scrollable-chart') as HTMLElement;
    return chartContainer.offsetWidth - 30;
  }

  get subjectCode() {
    return this.subject.code;
  }

  private get maxGrade() {
    return this.activeGradeList.reduce(
      (prev: any, current: any) => (prev.id > current.id) ?
        prev : current);
  }

  private get maxChartHeight() {
    let maxChartHeight = this.highestCompetencySize * this.expandedGraphCellHeight;
    maxChartHeight = this.chartHeight > maxChartHeight ? this.chartHeight : maxChartHeight;
    return maxChartHeight;
  }

  private get hasPrerequisites() {
    const prerequisites = this.prerequisites;
    const activeDomain = this.activeDomain;
    const isCompetency = this.isCompetencyActive;
    const hasAccess =  activeDomain.topics.find(
      (topic: any) =>  topic.competencies.find(
        (competency: any) => prerequisites.find(
          (preR: any) => preR.id === competency.competencyCode)));
    return (isCompetency && hasAccess) ? true : false;
  }

  private get isShowMarker() {
    const subjectFrwId = `${this.subject.frameworkId}.${this.subject.id}`;
    const session = sessionService.getSession();
    const tenantSettings = session!.tenant!.settings;
    const hasEnableMarker = tenantSettings ?
    (subjectFrwId.includes(tenantSettings!.enable_marker) ? true : false) : false;
    return hasEnableMarker;
  }

  @Prop()
  private userId!: string;

  @Prop()
  private subject!: SubjectModel;

  private proficiencyChartContainer!: any;

  private domainCompetencyMatrix!: any[];

  private domainTopicMetadata: any[] = [];

  private chartData?: any = [];

  private skylineContainer!: any;

  private gradelineContainer!: any;

  private expandedGraphCellHeight: number = 40;

  private topicCellHeight: number = 40;

  private markerList: MarkerListModel[] = [];

  private activeMarker: MarkerListModel = {} as MarkerListModel;

  private markerBoundaries: MarkerBoundaryModel[] = [];

  private markerSearchTerms: string = '';

  private prerequisiteContainer?: any;

  private cellWidth: number = 40;

  private cellHeight: number = 10;

  @Prop()
  private activeCompetency!: any;

  private maxDomainSize: number = 0;

  private isShowExpandedGraph: boolean = false;

  private taxonomyGrades: GradeModel[] = [];

  private gradeBoundaries!: TxGradeBoundary[];

  private activeGrade: any = {};

  private isLoading: boolean = false;

  private numberOfTopics = 1;

  private markerCount = 0;

  @Prop()
  private isGradeSelect?: boolean;

  @Prop()
  private isCompetencyActive: boolean = false;

  @Prop()
  private month?: string;

  @Prop()
  private year?: string;

  private totalCompetencies: number = 0;

  // @Prop()
  // private activeDomainSeq?: any = 0;

  @Prop({default: null})
  private activeDomain!: Domain;

  @Prop()
  private activeTopic: Topic | null = null;

  private activeCompetencyStyle: any = null;

  @Prop()
  private isCompetencyMap!: boolean;

  @Prop()
  private isDomainView?: boolean;

  private multiGradeActiveList: any = [];

  private activeGradeList: any = [];

  private activeMarkerList: any = [];

  @Prop()
  private prerequisites: any;

  @Prop()
  private isCompetencyView!: boolean;

  private isSelectedCompetency: boolean = false;

  @Prop()
  private selectedDomain: any;

  @Prop()
  private selectedCompetency: any;

  @Prop({default: false})
  private isDomainPanelEnabled!: boolean;

  private isToggleGrade: boolean = false;

  private isToggleMarker: boolean = false;

  private minGrade!: any;

  private selectedGradeCompetency: any = [];

  private skylinePoints: any = [];

  private isExpanding: boolean = false;

  private highestTopicSize: number = 0;

  private highestCompetencySize: number = 0;

  private fetchedGradeBoundaries: Array<{
    grade: number,
    boundaries: TxGradeBoundary[],
    isActive: boolean,
    boundaryPoints: any[],
  }> = [];

  private gradesBoundaryPoints: any[] = [];

  private isCloseOtherDomain: boolean = false;

  private frameworkList: any = [];

  private activeFramework: any = null;

  private crosswalkFCList: any = [];

  @Prop()
  private isGradeView!: boolean;

  private popupContent: any = null;

  @Watch('selectedCompetency')
  public onChangeSelectedCompetency(competency: any) {
    if (!this.isShowExpandedGraph) {
      this.isShowExpandedGraph = true;
    }
    this.selectCompetency(competency);
    this.isSelectedCompetency = true;
  }

  @Watch('prerequisites')
  public async onChangePrerequisite(value: any) {
    this.isLoading = false;
    // this.drawPrerequisitesLine();
    await this.parseDependencyDomain();
  }



  @Watch('isCompetencyActive')
  public async watchCompetencyClose(value: boolean) {

    if (!value && !this.isExpanding) {
      this.isExpanding = true;
      const activeDomains = this.chartData.filter(
        (item: any) => item.isExpanded && (item.domainSeq !== this.activeDomain!.domainSeq));
      for await (const domain of activeDomains) {
        await this.closeTimeout(50);
        await this.clearExisitingDomain(domain);
      }
      this.resetChart();
      this.isExpanding = false;
    }
  }

  @Watch('subjectCode')
  public onChangeSubject() {
    const component = this;
    component.loadChartData();
    component.loadTaxonomyGrades();
    component.searchMarkers();
    component.activeGrade = {};
  }

  @Watch('isDomainView')
  public watchDomainView(value: any) {
    if (value) {
      this.activeGradeList = [];
      this.activeMarkerList = [];
      this.minGrade = null;
      this.fetchedGradeBoundaries.map((gradeBoundariesItem) => gradeBoundariesItem.isActive = false);
      this.drawGradeBoundaryLine();
    }
  }

  public created() {
    this.isLoading = true;
    this.fetchFrameworkList().then(() => {
      this.loadTaxonomyGrades();
      this.searchMarkers();
    });
  }


  // -------------------------------------------------------------------------
  // Actions

  public onToggleGraphView() {
    const component = this;
    component.isShowExpandedGraph = !component.isShowExpandedGraph;
    component.isSelectedCompetency = false;
    this.skylinePoints.map((point: any) => point.isExpanded = component.isShowExpandedGraph);
    this.chartData.map((domain: Domain) => domain.isExpanded = component.isShowExpandedGraph);
    // component.drawProficiencyChart();
    // console.log('ontoggle graph view');
    this.drawDomainTopicCompetencyChart();
    this.handleDomainGradeTransition(undefined, component.isShowExpandedGraph);
    if (component.isCompetencyMap) {
      component.$emit('onChangeGraphView', component.isShowExpandedGraph);
      if (this.selectedDomain.domainSeq && this.isDomainView) {
        component.toggleActiveDomainBar(component.selectedDomain.domainSeq);
      }
    } else {
      // component.highlightCompetency(component.activeCompetency);
    }
    // NOTE need to enable once hi-line data ready
    // this.multiGradeActiveList.map((grade: any, index: any) => {
    //   component.drawGradeBoundaryLine(index);
    // });
    // component.drawGradeBoundaryLine();

    // if (component.activeDomainSeq) {
    //   component.toggleActiveDomainBar(component.activeDomainSeq);
    // }
  }

  public onRemoveGrade() {
    this.activeGradeList = [];
    this.multiGradeActiveList = [];
    this.selectedGradeCompetency = [];
    this.fetchedGradeBoundaries.forEach((gradeBoundariesItem) => {
      this.$set(gradeBoundariesItem, 'isActive', false);
    });
    d3.selectAll(`#gradeline-group g`).remove();
    d3.selectAll('.competency-cell').classed('masked-div', false);
  }

  public onSelectGrade(grade: GradeModel | any) {
    const component = this;
    component.multiGradeActiveList = [];
    component.activeGradeList = [];
    component.selectedGradeCompetency = [];

    // if ((!component.activeGradeList.length || (component.activeGradeList.length > 1))) {
    //   if (component.activeGradeList.length
    // && grade.id === component.activeGradeList[0].id && !this.isCompetencyMap) {
    //     component.activeGradeList = [];
    //   } else {
    //     component.activeGradeList = [];
    //     component.activeGradeList.push(grade);
    //   }
    // } else {
    //   component.activeGradeList[0].sequence > grade.sequence ?
    //      this.activeGradeList.push(grade) : this.activeGradeList.unshift(grade);
    // }
    component.activeGradeList = [grade];
    this.fetchedGradeBoundaries.map((gradeBoundariesItem) => gradeBoundariesItem.isActive = false);
    this.minGradeLine().then(() => {
      this.loadTaxonomyGradeBoundaries(this.activeGradeList[0].id).then(() => {
        this.drawGradeBoundaryLine();
        this.inBetweenGradeList();
      });
    });
  }

  public onExpandDomain(domain: Domain) {
    // this.$emit('onSelectdomain', domain)
    this.isExpanding = true;
    if (domain && domain.domainSeq) {
      this.$emit('onSelectDomain', domain);
    }
    this.isExpanding = false;
  }

  public async onCloseDomain(domain: Domain) {
    this.isExpanding = true;
    this.$emit('onCloseGrade');
    if (domain && domain.domainSeq) {
      this.resetChart();
      d3.select(`#domain-group-${domain.domainSeq} #active-competency`).remove();
      await this.handleDomainBarTransition(domain, domain.domainSeq, !domain.isExpanded);
      await this.handleDomainGradeTransition(domain.domainSeq);
      await this.closeTimeout(50);
      if (this.activeDomain && this.activeDomain!.domainSeq === domain.domainSeq) {
        this.$emit('onSelectDomain', domain);
      }
    }
    this.isExpanding = false;
  }

  public async closeActiveDomain(domain: any) {
    this.isExpanding = true;
    const activeDomains = this.chartData.filter(
      (item: any) => item.isExpanded);

    if (domain && domain.domainSeq) {
      if (activeDomains.length <= 1) {
        this.resetChart();
        this.$emit('onCloseGrade');
      }

      d3.select(`#domain-group-${domain.domainSeq} #active-competency`).remove();
      await this.handleDomainBarTransition(domain, domain.domainSeq, !domain.isExpanded);
      await this.handleDomainGradeTransition(domain.domainSeq);
      await this.closeTimeout(50);
      if (this.isCompetencyActive && this.hasPrerequisites && activeDomains.length > 1) {
        await this.drawPrerequisitesLine();
      } else {
        if (this.activeDomain && this.activeDomain!.domainSeq === domain.domainSeq) {
          this.$emit('onSelectDomain', domain);
        } else {
          this.$emit('onSelectDomain', null);
        }
      }

    }
    this.isExpanding = false;
  }

  public onSelectTopic(topic: Topic, domainSeq: number) {
    const domainTopics = [...document.querySelectorAll(`#domain-group-${domainSeq} #topic-group [id*="topic-"]`)];
    this.$emit('onSelectTopic', topic);
  }

  public backAction() {
    this.$emit('backAction');
  }

  public loadChartData() {
    const component = this;
    component.isLoading = true;
    axios.all([
      component.fetchUserDomainCompetencyMatrix(),
      component.getSubjectDomainTopicMetadata()]).then(axios.spread(
        (domainCompetencyMatrix: Domain[], domainTopicMetadata: Domain[]) => {
          component.domainTopicMetadata = domainTopicMetadata;
          component.domainCompetencyMatrix = domainCompetencyMatrix;
          component.parseDomainTopicCompetencyMatrix(domainCompetencyMatrix, domainTopicMetadata);
          component.isLoading = false;
          if (this.isCompetencyMap) {
            this.$emit('loadCompetencyMap', component.chartData);
          }
        }));
  }

  public loadTaxonomyGrades() {
    const component = this;
    return taxonomyAPI.fetchTaxonomyGrades(this.subjectCode).then((taxonomyGrades: GradeModel[]) => {
      component.taxonomyGrades = taxonomyGrades.sort((a: any, b: any) => a.sequence - b.sequence);
      component.loadChartData();
    });
  }

  public async loadTaxonomyGradeBoundaries(gradeId: number) {
    const component = this;
    const gradeBoundaries = this.fetchedGradeBoundaries.find(
      (gradeBoundariesItem) => gradeBoundariesItem.grade === gradeId);
    if (!gradeBoundaries) {
      return taxonomyAPI.fetchTaxonomyGradeBoundaries(gradeId).then((gradeBoundariesItem: TxGradeBoundary[]) => {
        component.fetchedGradeBoundaries.push({
          grade: gradeId,
          boundaries: gradeBoundariesItem,
          isActive: true,
          boundaryPoints: component.getGradeBoundaryPoints(gradeBoundariesItem),
        });
        // return this.drawGradeBoundaryLine();
      });
    } else {
      gradeBoundaries.isActive = true;
      // return this.drawGradeBoundaryLine();
    }
  }


  public drawDomainChart(domainChartData: any, domainSeq: number) {
    const component = this;
    const svg = d3.select('#domains-group');
    const cellWidth = component.cellWidth;
    const cellHeight = component.cellHeight;
    let competencySeq = -1;
    const domainGroup = svg.append('g').attr('id', `domain-group-${domainSeq}`);

    domainGroup.attr('transform', `translate(${cellWidth * (domainSeq - 1)}, 0)`);
    const domainCompetencyGroup = domainGroup.append('g').attr('id', 'competencies-group')
      .on('click', () => {
        this.$emit('onSelectDomain', domainChartData);
      });
    this.totalCompetencies += domainChartData.totalCompetencies;
    const groupedDomainCompetencies = new Array(domainChartData.totalCompetencies).fill({});
    const competencyCells = domainCompetencyGroup.selectAll('.competency').data(groupedDomainCompetencies);
    competencyCells
      .enter()
      .append('rect')
      .attr('class', (competency: any, index: number) => {
        const curPos = index + 1;
        const statusNumber = curPos <= domainChartData.masteredCompetencies ?
          '4' :
          curPos <= domainChartData.masteredCompetencies + domainChartData.inprogressCompetencies ?
            '1' :
            '0';
        return `competency-cell competency-${index + 1}
            ${this.isCompetencyMap ? ' competency-status-fill-5 ' : 'competency-status-fill-' + statusNumber}
            ${competency.isSkyLineCompetency ? 'skyline-competency' : ''}`;
      })
      .attr('width', cellWidth)
      .attr('height', cellHeight)
      .attr('x', 0)
      .attr('y', (competency: any, index: number) => {
        competencySeq++;
        return competencySeq * cellHeight;
      });
    competencyCells.exit().remove();

  }

  public getTranslation(transform: any) {
    // Create a dummy g for calculation purposes only. This will never
    // be appended to the DOM and will be discarded once this function
    // returns.
    const g: any = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    // Set the transform attribute to the provided string value.
    g.setAttributeNS(null, 'transform', transform);

    // consolidate the SVGTransformList containing all transformations
    // to a single SVGTransform of type SVG_TRANSFORM_MATRIX and get
    // its SVGMatrix.
    const matrix = g.transform.baseVal.consolidate().matrix;

    // As per definition values e and f are the ones for the translation.
    return [matrix.e, matrix.f];
  }

  public getHorizontalLinePoints(x1: number, y1: number, alignmentPixes: number = 0) {
    const component = this;
    const cellHeight = component.cellHeight;
    const cellWidth = component.cellWidth;
    y1 = y1 === 0 ? y1 : y1 + cellHeight;
    const x2 = x1 + cellWidth;
    const y2 = y1;
    return {
      x1,
      y1,
      x2,
      y2,
    };
  }

  /**
   * @function showDropShadow
   * Method to show a drop shadow in skyline
   */
  public addSkylineBackshadow() {
    const component = this;
    const skylineContainer = component.skylineContainer;
    const filterContainer = skylineContainer
      .append('defs')
      .append('filter')
      .attr('id', 'back-shadow');
    filterContainer
      .append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '0')
      .attr('stdDeviation', '4');
  }

  public fetchUserDomainCompetencyMatrix() {
    const month = Number(this.month);
    const year = Number(this.year);
    const params = {
      user: this.userId,
      subject: this.subjectCode,
      month,
      year,
    };
    // return competencyAPI.fetchUserDomainCompetencyMatrix(params);
    return competencyAPI.fetchDomainTopicCompetencyMatrix(params);
  }

  public getSubjectDomainTopicMetadata() {
    const params = {
      subject: this.subjectCode,
    };
    // return competencyAPI.getCompetencyMatrixCoordinates(params);
    return competencyAPI.fetchSubjectDomainTopicMetadata(params);
  }

  public selectCompetency(competency: any) {
    const component = this;
    this.isLoading = true;
    if (component.isShowExpandedGraph) {
      this.$emit('onSelectCompetency', competency);
    } else {
      const activeDomain = component.chartData.find((domain: DomainModel) => {
        return domain.domainCode === competency.domainCode;
      });
      // component.onSelectDomain(activeDomain);
    }
  }



  public drawGradeBoundaryLine(gradeIndex: any = null) {
    const component = this;
    const cellWidth = component.cellWidth;
    const cellHeight = component.cellHeight;
    const activatedGradeBoundaries = this.fetchedGradeBoundaries.filter((gradeBoundaries) => gradeBoundaries.isActive);
    d3.selectAll(`#gradeline-group g`).remove();
    d3.selectAll('.competency-cell').classed('masked-div', false);
    if (!activatedGradeBoundaries.length) {
      return;
    }
    const activeDomain =  this.chartData.find((item: any) => item.isExpanded);
    activatedGradeBoundaries.forEach(async (gradeBoundaryPoints, index: number) => {
      d3.select(`#gradeline-group grade-${gradeBoundaryPoints.grade}`).remove(); // Remove skyline, if exist
      let curBarPos = 0;
      let strokeDash = 0;
      const adjustableWidth = 10;
      let points = '';
      gradeBoundaryPoints.boundaryPoints.forEach((domain: any) => {
        if (activeDomain && activeDomain.domainSeq === domain.domainSeq) {
          // d3.selectAll('.competency-cell').classed('masked-div', false);
          const competencyStats = 0;
          domain.topics.map((topic: any) => {
            const x1 = curBarPos * this.cellWidth;
            const x2 = x1 + this.cellWidth;
            const y1 = topic.hiLineCompSeq * this.topicCellHeight;
            const y2 = y1;
            points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
            curBarPos++;
            strokeDash += (x1 === x2
              ? Math.max(y1, y2)
              : Math.max(x1, x2));
            // adjustableWidth = 0;

            // setTimeout(() => {
            //   const competencyCells = d3.selectAll(`#domain-group-${domain.domainSeq}
            //    #topic-${topic.topicSeq} .competency-cell`);
            //   competencyCells.each((item, pindex) => {
            //     if (activatedGradeBoundaries.length > 1) {
            //       if (index === 0 && pindex < topic.hiLineCompSeq) {
            //         d3.select(competencyCells.nodes()[pindex]).classed('masked-div', true);
            //       }
            //       if (index === 1 && pindex >= topic.hiLineCompSeq) {
            //         d3.select(competencyCells.nodes()[pindex]).classed('masked-div', true);
            //       }
            //     } else {
            //       if (pindex > topic.hiLineCompSeq) {
            //         d3.select(competencyCells.nodes()[pindex]).classed('masked-div', true);
            //       }
            //     }

            //   });
            // }, 2000);

          });
        } else {
          const numOfTopics = domain.topics.reduce((a: any, b: any) => a + b.hiLineCompSeq, 0);
          const x1 = curBarPos * this.cellWidth;
          const x2 = x1 + this.cellWidth;
          const y1 = numOfTopics * this.cellHeight;
          const y2 = y1;
          strokeDash += (x1 === x2
            ? Math.max(y1, y2)
            : Math.max(x1, x2));
          points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
          curBarPos++;

          // const competencyCells = d3.selectAll(`#domain-group-${domain.domainSeq}
          //   #competencies-group .competency-cell`);
          // competencyCells.each((item, elindex) => {
          //     if (activatedGradeBoundaries.length > 1) {
          //     if (index === 0 && elindex < numOfTopics) {
          //       d3.select(competencyCells.nodes()[elindex]).classed('masked-div', true);
          //     }
          //     if (index === 1 && elindex >= numOfTopics) {
          //       d3.select(competencyCells.nodes()[elindex]).classed('masked-div', true);
          //     }
          //   } else {
          //     if (elindex >= numOfTopics) {
          //       d3.select(competencyCells.nodes()[elindex]).classed('masked-div', true);
          //     }
          //   }
          //   });

        }
      });
      d3.select('#gradeline-group')
        .append('g')
        .attr('id', `grade-${gradeBoundaryPoints.grade}`)
        .append('polyline')
        .attr('points', points)
        .attr('class', `skyline ${(this.minGrade && gradeBoundaryPoints.grade === this.minGrade.id)
          ? 'low-line-grade' : ''} `)
        .attr('stroke-dasharray', strokeDash)
        .attr('stroke-dashoffset', strokeDash);
    });
    const gradeFilter = d3.select('#gradeline-group').append('defs')
      .append('filter')
      .attr('id', 'back-shadow');
    gradeFilter.append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '0')
      .attr('stdDeviation', '4');
  }

  public onRemoveMarker() {
    this.markerList.map((item) => item.isActive = false);
    this.activeMarkerList = [];
    d3.selectAll(`#gradeline-group g`).remove();
    d3.selectAll('.competency-cell').classed('masked-div', false);
  }

  private loadMarkerBoundaries(chartData: any) {
    const activeMarker = this.activeMarker;
    if (activeMarker) {
      const boundaries = markerSerilaizer.normalizeMarkerHighline(chartData, activeMarker);
      const markerBoundaryPoints = this.getGradeBoundaryPoints(boundaries);
      this.fetchedGradeBoundaries = [{
        grade: activeMarker.id,
        boundaries,
        isActive: true,
        boundaryPoints: markerBoundaryPoints,
      }];
    }
    this.drawGradeBoundaryLine();
  }

  private onChangeFW(framework: any) {
    this.activeFramework = framework;
    this.subject.frameworkId = framework.code || null;
    this.chartData = [];
    this.loadCrosswalkData().then(() => {
      this.loadChartData();
    });
  }

  private getGradeBoundaryPoints(gradeBoundaries: any[]) {
    const gradeBoundaryPoints: any[] = [];
    this.chartData.map((domainData: Domain) => {
      const domainBoundary = gradeBoundaries.filter(
        (domainGrade) => domainGrade.domainCode === domainData.domainCode);
      let domainHiLineCompSeq = 0;
      const domainTopicsGradeBoundaries: any = {
        domainSeq: domainData.domainSeq,
        topics: [],
        isExpanded: !!domainData.isExpanded,
        isHiLineAvailable: !!domainBoundary.length,
        hiLineCompSeq: domainHiLineCompSeq,
      };

      let isHiLineTopicCovered = false;
      let domainGradeBoundaries = gradeBoundaries.find(
        (domainGrade) => domainGrade.domainCode === domainData.domainCode);
      domainData.topics.map((topic: Topic | any) => {
        const topicBoundaries = domainBoundary.find(
          (item) => item.topicCode && item.topicCode === topic.topicCode,
        );
        if (topicBoundaries) {
          domainGradeBoundaries = topicBoundaries;
        }
        let hiLineCompSeq =   !isHiLineTopicCovered &&
          domainGradeBoundaries &&
          !domainGradeBoundaries.topicHighlineComp &&
          topic.competencies
          ? topic.competencies.length
          : 0;

        const topicBoundary: any = {
          topicSeq: topic.topicSeq,
          hiLineCompSeq,
        };
        if (  domainGradeBoundaries &&
          (topic.topicCode === domainGradeBoundaries.topicCode ||
            topic.topicCode === domainGradeBoundaries.highlineTopic)) {
          isHiLineTopicCovered = true;
          hiLineCompSeq = topic.competencies.findIndex(
            (competency: any) =>
              competency.competencyCode ===
              (domainGradeBoundaries.topicHighlineComp
                ? domainGradeBoundaries.topicHighlineComp
                : domainGradeBoundaries.highlineComp),
          ) + 1;
        }
        topicBoundary.hiLineCompSeq = hiLineCompSeq;
        domainTopicsGradeBoundaries.topics.push(topicBoundary);
        domainHiLineCompSeq = domainHiLineCompSeq < hiLineCompSeq ? hiLineCompSeq : domainHiLineCompSeq;
      });

      domainTopicsGradeBoundaries.hiLineCompSeq = domainHiLineCompSeq;
      gradeBoundaryPoints.push(domainTopicsGradeBoundaries);
    });
    return gradeBoundaryPoints;
  }

  @Watch('activeDomain')
  private async watchActiveDomain() {
    if (this.activeDomain && !this.isExpanding) {
      if ((!this.isCompetencyActive && !this.hasPrerequisites)
            || (!this.hasPrerequisites && this.isCompetencyActive)  ) {
        this.isExpanding = true;
        const activeDomains = this.chartData.filter(
          (item: any) => item.isExpanded && (item.domainSeq !== this.activeDomain!.domainSeq));
        for await (const domain of activeDomains) {
          await this.clearExisitingDomain(domain);
          await this.closeTimeout(50);
        }
        this.resetChart();
        this.isExpanding = false;
      }
      this.isCloseOtherDomain =  this.isCompetencyActive ? true : false;
      await this.closeTimeout(50);
      if (!this.isExpanding) {
        this.isExpanding = true;
        const activeDomainData = this.chartData.find(
          (domainData: Domain) => this.activeDomain && (domainData.domainSeq === this.activeDomain.domainSeq));
        await this.handleDomainBarTransition(activeDomainData,
          activeDomainData.domainSeq, !activeDomainData.isExpanded);
        await this.drawTopicChart(activeDomainData, activeDomainData.domainSeq);
        await this.handleDomainGradeTransition(activeDomainData.domainSeq);
        await this.closeTimeout(50);
        if (this.isCompetencyActive && this.hasPrerequisites) {
          this.drawPrerequisitesLine();
        }
        this.isExpanding = false;
      }


    } else {
      if (!this.isExpanding) {
        this.isExpanding = true;
        const activeDomains = this.chartData.filter(
          (item: any) => item.isExpanded || item.isActive);
        for await (const domain of activeDomains) {
          await this.clearExisitingDomain(domain);
        }
        this.isExpanding = false;
      }
    }

  }

  @Watch('activeCompetency')
  private onToggleCompetency() {
    if (!this.isDomainView && (this.isGradeSelect || this.isGradeView)) {
      const activeDomain = this.chartData.find((domain: any) => domain.domainSeq === this.activeCompetency.domainSeq);
      this.$emit('onSelectDomain', activeDomain);
      return;
    }
    this.highlightCompetency(this.activeCompetency);
  }

  @Watch('month')
  private onChangeTimeline() {
    const component = this;
    component.loadChartData();
  }

  private addDomaniBackShadow() {
    const group = d3.select('#domains-group')
      .append('defs')
      .append('filter')
      .attr('id', 'domain-shadow');
    group
      .append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '0')
      .attr('stdDeviation', '5');
  }

  private parseDomainTopicCompetencyMatrix(domainTopicMatrix: Domain[], domainTopicMetadata: Domain[]) {
    const chartData = [];
    this.maxDomainSize = Math.max.apply(Math, domainTopicMatrix.map((domain) => domain.totalCompetencies || 0));
    const totalCompetencies = 0;
    domainTopicMetadata.map((domainMetadata: Domain) => {

      const topicSkylinePoints: any[] = [];
      this.numberOfTopics += domainMetadata.topics.length;
      const domainMatrix = domainTopicMatrix.find((matrix: Domain) => matrix.domainCode === domainMetadata.domainCode);
      const parsedDomainData = { ...domainMetadata, ...domainMatrix };
      this.highestTopicSize = this.highestTopicSize < domainMetadata.topics.length ?
        domainMetadata.topics.length :
        this.highestTopicSize;
      if (domainMatrix && domainMatrix.topics) {
        const parsedDomainTopicData: Topic[] = [];
        const crossFWDomain = this.crosswalkFCList.find(
          (fcDomain: Domain) => fcDomain.domainCode === domainMetadata.domainCode);
        domainMatrix.topics.map((topicMatrix: Topic) => {
          topicMatrix.domainName = domainMetadata.domainName;
          const crossFWTopics = crossFWDomain && crossFWDomain.topics.find(
            (topic: Topic) => topic.topicCode === topicMatrix.topicCode);
          const topicMetadata = domainMetadata.topics.find((topic: Topic) => topic.topicCode === topicMatrix.topicCode);
          if (topicMatrix.competencies) {
            this.highestCompetencySize = this.highestCompetencySize < topicMatrix.competencies.length
              ? topicMatrix.competencies.length : this.highestCompetencySize;
            topicMatrix.competencies.map((competency: Competency) => {
              competency.domainCode = domainMetadata.domainCode,
                competency.domainSeq = domainMetadata.domainSeq,
                competency.topicCode = topicMatrix.topicCode,
                competency.topicSeq = topicMatrix.topicSeq,
                competency.competencyStatus = this.isCompetencyMap ? 2 : competency.status;
              competency.isMastered = competency.status > 1;
              competency.isInferred = competency.status === 2 || competency.status === 3;
              competency.isSkyLineCompetency = false;
              competency.isGradeBoundary = false;
              if (crossFWTopics && crossFWTopics.competencies) {
                const crossFWcompetency = crossFWTopics.competencies.find(
                  (comptcy: Competency) => comptcy.competencyCode === competency.competencyCode);
                if (crossFWcompetency) {
                  competency.competencyDisplayCode = crossFWcompetency.frameworkCompetencyDisplayCode;

                  competency.framework = crossFWcompetency.frameworkCompetencyCode
                    ? getFWK(crossFWcompetency.frameworkCompetencyCode) : null;
                  competency.isHighlighted = true;
                }
              }
            });
            topicMatrix.competencies[0].isSkyLineCompetency = true;
          }

          topicSkylinePoints.push({
            topicSeq: topicMatrix.topicSeq,
            skylineCompetencySeq: (topicMatrix.masteredCompetencies || 0) + (topicMatrix.inferredCompetencies || 0),
          });
          parsedDomainTopicData.push({ ...topicMatrix, ...topicMetadata });
        });
        parsedDomainData.topics = parsedDomainTopicData;
      }

      this.skylinePoints.push({
        domainSeq: parsedDomainData.domainSeq,
        skylineCompetencySeq: parsedDomainData.masteredCompetencies,
        topicSkylinePoints,
      });
      this.chartData.push(parsedDomainData);
    });
    if (this.activeMarker) {
      this.loadMarkerBoundaries(this.chartData);
    }
    this.drawDomainTopicCompetencyChart();
  }

  private drawDomainTopicCompetencyChart() {
    const component = this;
    const proficiencyChartData = component.chartData;
    const chartHeight = component.chartHeight;
    const chartWidth = component.chartWidth;
    component.cellHeight = this.isShowExpandedGraph ? 40 : chartHeight / component.maxDomainSize;
    component.cellWidth = (chartWidth / proficiencyChartData.length) > 200
      ? 200 : (chartWidth / proficiencyChartData.length);
    d3.select('svg#chart-graph').remove();

    const svg = d3.select('#chart-area')
      .append('svg')
      .attr('id', 'chart-graph')
      .attr('width', component.cellWidth * proficiencyChartData.length)
      .attr('height', chartHeight);
    svg.append('g').attr('id', 'domains-group');
    component.skylineContainer = svg.append('g').attr('id', 'skyline-group');
    component.gradelineContainer = svg.append('g').attr('id', 'gradeline-group');
    component.prerequisiteContainer = svg.append('g').attr('id', 'prerequisite-group');
    component.proficiencyChartContainer = svg;
    this.addDomaniBackShadow();
    if (this.isShowExpandedGraph) {
      this.drawExpandedChart(proficiencyChartData);
    } else {
      proficiencyChartData.map((domainChartData: any, seq: number) => {
        component.drawDomainChart(domainChartData, seq + 1);
      });
    }

    if (!this.isSelectedCompetency) {
      const chartContainer = component.$el.querySelector('.scrollable-chart') as HTMLElement;
      chartContainer.scrollTop = chartHeight;
    }
    component.isLoading = false;
  }

  private drawExpandedChart(proficiencyChartData: any) {
    const svg = d3.select('#domains-group');
    const cellWidth = this.cellWidth;
    const cellHeight = this.cellHeight;
    let numberOfColumns = 1;
    this.isExpanding = true;
    proficiencyChartData.map(async (domainData: any) => {
      const domainGroup = svg.append('g').attr('id', `domain-group-${domainData.domainSeq}`);
      domainGroup.attr('transform', `translate(${cellWidth * (numberOfColumns - 1)}, 0)`);
      this.chartData[domainData.domainSeq - 1].isExpanded = true;
      numberOfColumns += domainData.topics.length;
      const domainCompetencyGroup = domainGroup.append('g').attr('id', 'competencies-group')
        .on('click', async () => {
          await this.handleDomainBarTransition(domainData, domainData.domainSeq);
          await this.drawTopicChart(domainData, domainData.domainSeq);
          await this.handleDomainGradeTransition(domainData.domainSeq, true);
        });

      await this.drawTopicChart(domainData, domainData.domainSeq);
      await this.highlightDomain(domainData.domainSeq, true);
    });
    // this.drawProficiencySkyline();
    this.toggleChartSize(this.numberOfTopics, true, false);
    this.isExpanding = false;
  }

  private async drawTopicChart(domain: Domain, domainSeq: number) {
    const component = this;
    return new Promise<void>(async (resolve) => {
      const domainGroup = d3.select(`#domain-group-${domainSeq}`);
      domainGroup.select('#topic-group').remove();
      const topicGroup = domainGroup.append('g').attr('id', `topic-group`);
      let seq = 0;
      for await (const topic of domain.topics) {
        const topicContainer = topicGroup.append('g').attr('id', `topic-${seq + 1}`)
          .attr('transform', `translate(${seq * this.cellWidth}, 0)`);
        if (topic.competencies) {
          const competencyCells = topicContainer.selectAll('.competency').data(topic.competencies);
          competencyCells.enter()
            .append('rect')
            .on('click', (competency: Competency) => {
              this.$emit('onSelectCompetency', competency);
              if (this.isCompetencyMap) {
                this.isSelectedCompetency = true;
              }
            })
            .on('mousemove', (d) => {
              const e = d3.event;
              d3.selectAll('.comp-code-popup')
                .style('left', `${e.pageX + 20}px`).style('top', `${e.pageY + 20}px`);
              component.popupContent = d;
            })
            .on('mouseout', () => {
              component.popupContent = null;
            })
            .transition()
            .duration(this.isCloseOtherDomain ? 20 : 1000)
            .delay(this.isCloseOtherDomain ? 10 : 800)
            .attr('id', (competency: Competency) => {
              return `competency-cell-${competency.competencySeq}`;
            })
            .attr('width', this.cellWidth)
            .attr('height', this.topicCellHeight)
            .attr('x', 0)
            .attr('y', (d: any, i: number) => {
              return i * this.topicCellHeight;
            })
            .attr('class', (competency: any) => {
              return `competency-cell ${this.isCompetencyMap ? ' competency-status-fill-5 ' : 'competency-status-fill-' + competency.status}
            ${competency.isSkylineCompetency ? 'skyline-competency' : ''} ${competency.isHighlighted ? '' : 'shadow-div'}`;
            });
          competencyCells.exit().remove();
        }
        seq++;
      }
      // console.log(domainSeq, "---topic")
      return resolve();
    });

  }

  private async handleDomainBarTransition(domain: Domain, domainSeq: number, isExpand: boolean = true) {
    return new Promise<void>(async (resolve) => {
      let lastMovingDomain = isExpand ? this.chartData.length : domainSeq + 1;
      const numberOfColumns = domain.topics.length > 1 ? domain.topics.length : 0; // domain seq * no.of topics
      const activeDomainHighlight = 20;
      if (numberOfColumns > 1) {
        while ((lastMovingDomain > domainSeq && isExpand) || (!isExpand && lastMovingDomain <= this.chartData.length)) {
          const domainGroup = d3.select(`#domain-group-${lastMovingDomain}`);
          const traslation = this.getTranslation(domainGroup.attr('transform'));
          const xValue = isExpand ?
            traslation[0] + ((numberOfColumns - 1) * this.cellWidth) :
            traslation[0] - ((numberOfColumns - 1) * this.cellWidth);
          const domainPosTo = isExpand ? ((lastMovingDomain - 1) + (numberOfColumns - 1)) : lastMovingDomain - 1;
          // Required while adding gap between domain panel
          const adjustedPos = isExpand ? xValue + activeDomainHighlight : xValue - activeDomainHighlight;
          domainGroup.transition()
            .duration(this.isCloseOtherDomain ? 0 : 800)
            .attr('transform', `translate(${xValue}, 0)`);
          isExpand ? lastMovingDomain-- : lastMovingDomain++;
        }
      }
      await this.toggleChartSize((isExpand ? domain.topics.length : numberOfColumns), isExpand);
      await this.toggleDomainGroup(domainSeq, isExpand);
      await this.highlightDomain(domainSeq, isExpand);
      resolve();
    });

  }

  private async highlightDomain(domainSeq: number, isExpand: boolean) {
    return new Promise<void>((resolve) => {
      const domainGroup = d3.select(`#domain-group-${domainSeq}`);
      domainGroup.attr('class', isExpand ? 'active' : '');
      const chartContainer = this.$el.querySelector('.scrollable-chart') as HTMLElement;
      if (isExpand) {
        domainGroup.append('foreignObject')
          .attr('class', 'domain-highlight')
          .attr('width', (this.chartData[domainSeq - 1].topics.length * this.cellWidth) + 'px')
          .attr('height', this.maxChartHeight);
      } else {
        domainGroup.select('foreignObject').remove();
      }
      return resolve();

    });
  }

  private async toggleChartSize(numberOfColumns: number = 0, isExpand: boolean, isAdjust: boolean = true) {
    return new Promise<void>((resolve) => {
      if (numberOfColumns > 1) {
        const chartWidth = parseFloat(d3.select('#chart-graph').attr('width'));
        const adjustableWidth = this.cellWidth * (numberOfColumns - 1);
        let updatedWidth = adjustableWidth;
        if (isAdjust) {
          updatedWidth = isExpand ? chartWidth + adjustableWidth : chartWidth - adjustableWidth;
        }
        d3.select('#chart-graph').attr('width', updatedWidth);
      }
      d3.select('#chart-graph').attr('height', this.chartHeight);
      // Auto scroll domain group
      if (isExpand) {
        this.$nextTick(() => {
          const domainGroupContainer = this.$el
            .querySelector(`#domain-group-${this.activeDomain.domainSeq}`) as HTMLElement;
          const graphVisual = this.$el.querySelector('.graph-visual') as HTMLElement;
          graphVisual.scrollLeft = this.getTranslation(domainGroupContainer.getAttribute('transform'))[0];
        });
        d3.select('#chart-graph').attr('height', this.maxChartHeight);
        const chartContainer = this.$el.querySelector('.scrollable-chart') as HTMLElement;
        chartContainer.scrollTop = this.maxChartHeight;
      }
      return resolve();
    });
  }


  private async toggleDomainGroup(domainSeq: number, isExpand: boolean) {
    return new Promise<void>((resolve) => {
      const domainGroup = d3.select(`#domain-group-${domainSeq}`);
      this.chartData.map((domain: Domain) => domain.isActive = false);
      if (isExpand) {
        domainGroup.select('#competencies-group').attr('class', 'hidden');
        domainGroup.select('#topic-group').attr('class', 'show');
        this.skylinePoints[domainSeq - 1].isExpanded = true;
        this.chartData[domainSeq - 1].isExpanded = true;
        this.chartData[domainSeq - 1].isActive = true;
      } else {
        domainGroup.select('#competencies-group').attr('class', 'show');
        domainGroup.select('#topic-group').attr('class', 'hidden');
        this.skylinePoints[domainSeq - 1].isExpanded = false;
        this.chartData[domainSeq - 1].isExpanded = false;
      }
      return resolve();
    });
    // this.drawProficiencySkyline();
  }

  private onClearGrade() {
    d3.select('#gradeline-group').remove();
  }

  private drawProficiencySkyline() {
    const component = this;
    const skylineElements: any = component.$el.querySelectorAll('.skyline-competency');
    const cellWidth = component.cellWidth;
    const cellHeight = component.cellHeight;

    let points = '';

    d3.select('#skyline-group polyline').remove(); // Remove skyline, if exist

    let curBarPos = 0;
    let strokeDash = 0;
    let adjustableWidth = 10;
    this.skylinePoints.forEach((domain: any) => {
      if (domain.isExpanded) {
        // Get topic bar competency pos
        adjustableWidth = 10;
        domain.topicSkylinePoints.map((topic: any, index: number) => {
          let end = 0;
          if (index === domain.topicSkylinePoints.length - 1) {
            // adjustableWidth;
            end = 10;
          }
          const x1 = curBarPos * this.cellWidth;
          const x2 = x1 + this.cellWidth;
          const y1 = topic.skylineCompetencySeq * this.topicCellHeight;
          const y2 = y1;


          points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
          curBarPos++;
          strokeDash += (x1 === x2
            ? Math.max(y1, y2)
            : Math.max(x1, x2));

        });
      } else {
        const x1 = curBarPos * this.cellWidth;
        const x2 = x1 + this.cellWidth;
        const y1 = domain.skylineCompetencySeq * this.cellHeight;
        const y2 = y1;
        strokeDash += (x1 === x2
          ? Math.max(y1, y2)
          : Math.max(x1, x2));
        points += ` ${x1 + ',' + y1} ${x2 + ',' + y2}`;
        curBarPos++;
      }
    });

    const skylineContainer = component.skylineContainer;

    d3.select('#skyline-group')
      .append('polyline')
      .attr('points', points)
      .attr('class', 'skyline')
      .attr('stroke-dasharray', strokeDash)
      .attr('stroke-dashoffset', strokeDash);
    component.addSkylineBackshadow();
  }

  private async handleDomainGradeTransition(domainSeq?: number, isExpand: boolean = true) {
    return new Promise((resolve) => {
      const activatedGradeBoundaries = this.fetchedGradeBoundaries.filter(
        (gradeBoundaries) => gradeBoundaries.isActive);
      const expandedDomainsSeq: any[] = [];
      this.chartData.map((data: Domain) => {
        if (data.isExpanded) {
          expandedDomainsSeq.push(data.domainSeq);
        }
      });
      activatedGradeBoundaries.map((gradeBoundaries) => {
        gradeBoundaries.boundaryPoints.map((boundaryPoints) => {
          if ((domainSeq && domainSeq === boundaryPoints.domainSeq)
            || !domainSeq || expandedDomainsSeq.includes(boundaryPoints.domainSeq)) {
            boundaryPoints.isExpanded = isExpand;
          }
        });
      });
      // console.log("handleDomainGradeTransition")
      resolve(this.drawGradeBoundaryLine());
    });
  }

  private toggleActiveDomainBar(seq: number, isActive: boolean = true) {
    const component = this;
    component.domainTopicMetadata.forEach((domainCoOrdinate: any) => {
      const domainBar: any = component.$el.querySelector(`#domain-group-${domainCoOrdinate.domainSeq}`);
      domainBar.classList.remove('non-active', 'active');
      if (isActive) {
        domainBar.classList.add('non-active');
      }
    });
    if (seq > 0) {
      const activeDomainBar: any = component.$el.querySelector(`#domain-group-${seq}`);
      activeDomainBar.classList.add('active');
      activeDomainBar.classList.remove('non-active');
    }
  }

  private highlightCompetency(competency: Competency) {
    const component = this;
    if (competency) {
      const competencyContainer: any = component.$el.querySelector(`
      #domain-group-${competency.domainSeq} #topic-${competency.topicSeq}
        #competency-cell-${competency.competencySeq}`) as HTMLElement;
      d3.select(`#domain-group-${competency.domainSeq} #active-competency`).remove();
      d3.select(`#domain-group-${competency.domainSeq}`)
        .append('g')
        .attr('id', 'active-competency')
        .attr('transform', `translate(${(competency.topicSeq ? competency.topicSeq - 1 : 0) * this.cellWidth}, ${competencyContainer.getAttribute('y')})`)
        .append('foreignObject')
        .attr('class', `active-competency competency-status-fill-${competency.competencyStatus}`)
        .attr('width', this.cellWidth)
        .attr('height', this.topicCellHeight);
    }
  }

  private isActiveGradeList(value: any) {
    return this.activeGradeList.find((grade: any) => grade.id === value.id) ||
      (this.activeGradeList.length > 1 &&
        this.activeGradeList[0].id > value.id && this.activeGradeList[1].id < value.id);
  }

  private drawPrerequisitesLine() {
    const cellWidth = this.cellWidth;
    const cellHeight = this.cellHeight;
    const prerequisiteContainer = this.prerequisiteContainer;
    prerequisiteContainer.select('g').remove();
    const prerequisites = this.prerequisites;
    const linePoints: any = [];
    const chartData = this.chartData;
    let domainPosition = 0;
    const activeComp = this.activeCompetency;
    prerequisiteContainer.append('g');
    d3.selectAll('.competency-cell').classed('masked-div', true);
    chartData.forEach((domain: any, domainIndex: any) => {
      domain.topics.forEach((topic: any) => {
        let competencyCount = 0;
        topic.competencies.forEach((competency: any) => {
          const preComp = prerequisites.find((item: any) => item.id === competency.competencyCode);
          if (preComp ||
            (activeComp.domainSeq === domain.domainSeq
              && activeComp.topicSeq === topic.topicSeq
              && activeComp.competencySeq === competency.competencySeq)) {
            let x = 0;
            let y = 0;
            const currentHeight = domain.isExpanded  ? this.topicCellHeight : this.cellHeight;
            x = domainPosition * cellWidth + (cellWidth / 2);
            y = (competencyCount * currentHeight) + (currentHeight / 2);
            linePoints.push({x, y});

            prerequisiteContainer.select('g')
              .append('circle')
              .attr('class', 'pre-connect')
              .attr('cx', x)
              .attr('cy', y)
              .attr('r', 5)
              .style('fill', '#fff');

            d3.select(`#domain-group-${domain.domainSeq} #topic-${topic.topicSeq} #competency-cell-${competency.competencySeq}`)
              .classed('masked-div', false);
          }
          competencyCount++;
        });
        if (domain.isExpanded) { domainPosition++; }
      });
      if (!domain.isExpanded) { domainPosition++; }
    });

    const line = d3.line()
      .x((d: any) => d.x)
      .y((d: any) => d.y)
      .curve(d3.curveLinear);

    const path = prerequisiteContainer.select('g').append('path')
      .attr('d', line(linePoints))
      .attr('stroke', '#fff')
      .attr('stroke-width', 4)
      .attr('fill', 'none');

    const totalLength = path.node().getTotalLength();
    path
      .attr('stroke-dasharray', totalLength + ' ' + totalLength)
      .attr('stroke-dashoffset', totalLength)
      .transition(200)
      .duration(600)
      .attr('stroke-dashoffset', 0);

  }

  private async parseDependencyDomain() {
    // const chartData = this.chartData;
    // const prerequisites = this.prerequisites;
    // this.isCloseOtherDomain = true;
    await this.drawPrerequisitesLine();
    // this.isCloseOtherDomain = false;
    await this.closeTimeout(50);
    // for await (const domainData of chartData) {
    //     const hasPrerequisites = prerequisites.find(
    //         (prerequisite: any) => domainData.topics.find(
    //           (topic: any) => topic.competencies.find(
    //             (competency: any) => competency.competencyCode === prerequisite.id)));
    //     this.isExpanding = true;
    //     if (hasPrerequisites && !domainData.isExpanded) {
    //       await this.handleDomainBarTransition(domainData, domainData.domainSeq, !domainData.isExpanded);
    //       await this.drawTopicChart(domainData, domainData.domainSeq);
    //       await this.handleDomainGradeTransition(domainData.domainSeq);
    //       await this.closeTimeout(50);
    //     }
    //     this.isExpanding = false;
    //   }

  }
  private closeTimeout(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private minGradeLine() {
    return new Promise<void>((resolve) => {
      const minGrade = this.activeGradeList.reduce(
        (prev: any, current: any) => (prev.sequence < current.sequence) ? prev : current);
      const minGradeIndex = this.taxonomyGrades.findIndex((grade) => grade.id === minGrade.id);
      const prevGrade: any = minGradeIndex ? this.taxonomyGrades[minGradeIndex - 1] : null;
      if (prevGrade) {
        this.minGrade = prevGrade;
        resolve(this.loadTaxonomyGradeBoundaries(prevGrade.id));
      } else {
        return resolve();
      }
    });
  }

  private async clearExisitingDomain(domain: any) {
    this.isCloseOtherDomain = true;
    return new Promise((resolve) => {
      return resolve(this.onCloseDomain(domain));
    });
  }

  private async inBetweenGradeList() {
    const activatedGradeBoundaries = this.fetchedGradeBoundaries.filter((gradeBoundaries) => gradeBoundaries.isActive);
    const highLineGB = activatedGradeBoundaries.find((item: any) => item.grade === this.maxGrade.id);
    const lowLineGB = activatedGradeBoundaries.find((item: any) => this.minGrade && item.grade === this.minGrade.id);
    const chartData = this.chartData;
    const betweenCompetencies: any = [];
    for await (const domain of chartData) {
      const domainCallBack = (item: any) => item.domainSeq === domain.domainSeq;
      const highLineDomain = highLineGB!.boundaryPoints.find(domainCallBack);
      const lowLineDomain = lowLineGB ? lowLineGB!.boundaryPoints.find(domainCallBack) : null;
      for await (const topic of domain.topics) {
        const topicCallBack = (item: any) => item.topicSeq === topic.topicSeq;
        const highLineComp = highLineDomain.topics.find(topicCallBack).hiLineCompSeq;
        const lowLineComp = lowLineDomain ? lowLineDomain.topics.find(topicCallBack)!.hiLineCompSeq : null;
        for await (const competency of topic.competencies) {
          const compSeq = competency.competencySeq;
          if (compSeq <= highLineComp && ((lowLineComp && lowLineComp <= compSeq) || !lowLineComp)) {
            betweenCompetencies.push(competency);
          }
        }
      }
    }
    this.$emit('onSelectGrade', betweenCompetencies, this.activeGradeList);
  }

  private resetChart() {
    const prerequisiteContainer = this.prerequisiteContainer;
    prerequisiteContainer.select('g').remove();
    d3.selectAll('.competency-cell').classed('masked-div', false);
    d3.selectAll(`#active-competency`).remove();
  }

  private fetchFrameworkList() {
    return taxonomyAPI.fetchFrameworks(this.subjectCode).then((framework) => {
      this.frameworkList = framework;
      this.activeFramework = framework[0];
      this.subject.frameworkId = this.activeFramework.code || null;
      this.loadCrosswalkData();
    });
  }

  private loadCrosswalkData() {
    return taxonomyAPI.fetchFWCrosswalk(this.activeFramework.code, this.subjectCode).then((crosswalkList) => {
      this.crosswalkFCList = crosswalkList;
    });
  }

  private async searchMarkers() {
    this.isLoading = true;
    const params: any = {
      filters: {
        markerFwCode: this.subject.frameworkId, // 'TMA'this.subject.frameworkId,
        markerSubject: this.subjectCode,
        markerSource: 'grade',
      },
      q: '*',
      pageNum: 0,
      limit: 20,
    };
    if (this.markerSearchTerms) {
      params.query = this.markerSearchTerms;
    }
    markersAPI.searchMarkers(params).then((marker: any) => {
      this.isLoading = false;
      if (!isAPICatchError(marker)) {
        const markerList = marker.markerList.filter((mItem: any) => mItem.markerSource === 'grade');
        const markerCount = marker.totalCount;
        if (markerCount > 0 && markerCount > marker.markerList.length) {
          params.pageNum = 0;
          this.isLoading = true;
          params.limit = markerCount;
          markersAPI.searchMarkers(params).then((markerData: any) => {
            this.isLoading = false;
            if (!isAPICatchError(markerData)) {
              this.markerList = markerData.markerList.filter((mrItem: any) => mrItem.markerSource === 'grade');
              sortByProperty(this.markerList, 'sequence', 'ASC');
              this.markerCount = markerData.totalCount;
            } else {
              if (markerList.length > 0) {
                sortByProperty(markerList, 'sequence', 'ASC');
              }
              this.markerCount = markerCount;
              this.markerList = markerList;
            }
          });
        } else {
          if (markerList.length > 0) {
            sortByProperty(markerList, 'sequence', 'ASC');
          }
          this.markerCount = markerCount;
          this.markerList = markerList;
        }
      }
    });
  }

  private onSelectMarker(activeMarker: any) {
    this.activeMarkerList = [];
    this.isToggleMarker = false;
    this.markerList.map((item) => item.isActive = false);
    activeMarker.isActive = true;
    this.activeMarkerList = [activeMarker];
    this.activeMarker = activeMarker;
    if (this.activeMarker) {
      this.loadMarkerBoundaries(this.chartData);
    }
  }

}
